import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  Collapsible,
  cn,
  Spinner,
} from 'ui';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarTrigger,
} from '../../../packages/ui/components/SideBar';
import { Calendar, Home, Inbox, Search, Settings } from 'lucide-react';
import {
  PathSlugs,
  PiramidLogo,
  useApiError,
  useEvaluatePathSlug,
  useSharedAuth,
} from 'shared-components';
import {
  IconCaretDown,
  IconChevronDown,
  IconFocusCentered,
  IconHourglassEmpty,
  IconRadar2,
  IconSquare,
  IconTimeline,
  IconUserSquare,
} from '@tabler/icons-react';
// import { useAuth } from '@/contexts/AuthContext';
import { buildUserFullName, SCROLL_CONTAINER_ATTRIBUTE } from 'utils';
import Link from 'next/link';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { IconUrgent } from '@tabler/icons-react';
import { CollapsibleContent, CollapsibleTrigger } from 'ui';
import { useNavigationTransition } from '../hooks/useNavigationTransition';
import { IconCaretDownFilled } from '@tabler/icons-react';
import { Icon } from '@tabler/icons-react';

const LogoutButton = ({ logoutUser }: { logoutUser: () => Promise<any> }) => {
  const router = useRouter();

  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = useState(false);

  const { handleError } = useApiError();

  const onLogout = useCallback(() => {
    setIsLoading(true);
    logoutUser()
      .then(() => {
        queryClient.clear();

        window.location.href = '/login';
      })
      .catch(handleError);
  }, [logoutUser]);

  return (
    <DropdownMenuItem onClick={onLogout} className='px-3'>
      <span className='text-base_brand text-foreground'>Cerrar sesión</span>
    </DropdownMenuItem>
  );
};

const useGetAllSearchParams = () => {
  const searchParams = useSearchParams();
  const params: { [anyProp: string]: string } = {};

  searchParams.forEach((value, key) => {
    params[key] = value;
  });

  return params;
};

export const buildQueryString = (obj: any) => {
  const queryString = Object.keys(obj)
    .filter(
      (key) => obj[key] !== '' && obj[key] !== undefined && obj[key] !== null,
    )
    .map(
      (key: string) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`,
    )
    .join('&');

  return queryString;
};

const iconStrokeWidth = 1.7;

// Whether a menu item should render as active. The root href ('/') is a prefix
// of every path, so a plain startsWith would keep it permanently active — match
// it (and the team_id case) exactly; every other item stays prefix-based so
// nested detail routes still highlight their parent nav entry.
const isMenuItemActive = (
  fullPath: string,
  href: string,
  exact: boolean,
): boolean => {
  if (exact || href === '/') return fullPath === href;
  return fullPath.startsWith(href);
};

interface MenuItemBase {
  title: string;
  icon: Icon;
  show?: boolean;
}

interface MenuItemLink extends MenuItemBase {
  href: string;
  collapsible?: false | null;
  onClick?: () => void;
  // Match this entry only on an exact path. Use it when its href is a prefix of
  // a sibling's (e.g. `/providers` vs `/providers/groups`) so both don't
  // highlight at once.
  exact?: boolean;
}

interface MenuItemCollapsible extends MenuItemBase {
  collapsible: true;
  items: MenuItemLink[];
}

// Encabezado de grupo: no navega ni tiene icono, solo rotula los items que le
// siguen. `label` discrimina esta variante del resto del union.
interface MenuItemLabel {
  label: string;
  show?: boolean;
}

export type MenuItem = MenuItemLink | MenuItemCollapsible | MenuItemLabel;

const MenuItemLinkContent = ({
  item,
  isPending,
}: {
  item: MenuItemLink;
  isPending: boolean;
}) => (
  <>
    <item.icon
      strokeWidth={iconStrokeWidth}
      className='text-foreground !size-[1.125rem]'
    />
    <span className='font-medium text-foreground text-base_brand'>
      {item.title}
    </span>
    {isPending && <Spinner className='m-0 ml-auto' size='xs' />}
  </>
);

const isMenuItemLabel = (item: MenuItem): item is MenuItemLabel =>
  'label' in item;

export interface SidebarComponents {
  headerCorner?: React.FC;
  logo?: React.FC;
  headerTitle?: React.ReactNode;
  // Pie del sidebar, anclado abajo. Opcional: las apps que no lo pasan renderizan
  // el sidebar como hasta ahora.
  footer?: React.FC;
}

const AppSidebar = ({
  logoutUser,
  items,
  components,
}: {
  logoutUser: () => Promise<any>;
  items: MenuItem[];
  components?: SidebarComponents;
}) => {
  const auth = useSharedAuth();

  const pathname = usePathname();
  const searchParams = useSearchParams();

  const allSearchParams = useGetAllSearchParams();

  const queryString = buildQueryString(allSearchParams);

  const fullPath = `${pathname}${queryString ? `?${queryString}` : ''}`;

  const { getLinkProps, pendingHref } = useNavigationTransition();

  const handleMenuLinkClick = useCallback(
    (item: MenuItemLink) => (event: React.MouseEvent<HTMLAnchorElement>) => {
      item.onClick?.();
      getLinkProps(item.href).onClick(event);
    },
    [getLinkProps],
  );

  return (
    <Sidebar>
      {/* A shrinkable column, so a long menu scrolls inside `SidebarContent`
          instead of pushing the footer below a short screen. */}
      <div className='flex min-h-0 flex-1 flex-col space-y-[1.875rem] px-[0.875rem] py-[1.125rem]'>
        <SidebarHeader className='p-0'>
          <SidebarMenu>
            <SidebarMenuItem className='flex flex-row items-center justify-between'>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton className='flex items-center justify-between gap-2'>
                    <div className='flex items-center gap-2'>
                      {components?.logo ? <components.logo /> : <PiramidLogo />}

                      <span className='text-base_brand font-semibold text-foreground max-w-32 truncate'>
                        {components?.headerTitle ||
                          (auth.user.first_name
                            ? buildUserFullName(auth.user)
                            : auth.user.email)}
                      </span>
                    </div>
                    <IconChevronDown className='!size-[0.625rem] text-base_brand flex-shrink-0' />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent side='bottom' align='start'>
                  {!components?.headerTitle ? (
                    <div className='text-base_brand pt-2 px-3'>
                      <p className='leading-[0.813rem] truncate text-[#A8A29E]'>
                        {auth.user.email}
                      </p>
                    </div>
                  ) : (
                    <div className='text-base_brand pt-3 px-3'>
                      <p className='leading-[0.813rem] truncate text-foreground'>
                        {buildUserFullName(auth.user)} <br />
                        <span className='text-[0.688rem] text-[#A8A29E]'>
                          {auth.user.email}
                        </span>
                      </p>
                    </div>
                  )}
                  <DropdownMenuSeparator className='mt-2' />

                  <DropdownMenuItem asChild className='px-3'>
                    <Link href='/settings'>
                      <span className='text-base_brand text-foreground'>
                        Configuración
                      </span>
                    </Link>
                  </DropdownMenuItem>
                  <LogoutButton logoutUser={logoutUser} />
                </DropdownMenuContent>
              </DropdownMenu>
              {components?.headerCorner && <components.headerCorner />}
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarHeader>
        <SidebarContent>
          <SidebarMenu>
            {items
              .filter((item) => item.show !== false)
              .map((item, index) => {
                if (isMenuItemLabel(item))
                  return (
                    <SidebarGroupLabel key={item.label} className='mt-[1rem]'>
                      {item.label}
                    </SidebarGroupLabel>
                  );

                if (item.collapsible)
                  return (
                    <Collapsible
                      key={item.title}
                      defaultOpen
                      className='group/collapsible'
                    >
                      <SidebarMenuItem>
                        <CollapsibleTrigger
                          className='[&[data-state=open]>svg]:rotate-180'
                          asChild
                        >
                          <SidebarMenuButton>
                            <item.icon
                              strokeWidth={iconStrokeWidth}
                              className='!rotate-0'
                            />
                            <span className='font-medium text-foreground text-base_brand'>
                              {item.title}
                            </span>
                            <IconCaretDownFilled
                              style={{
                                width: '0.75rem',
                                height: '0.75rem',
                              }}
                              className='!size-3 text-foreground'
                            />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent className='overflow-x-hidden'>
                          <SidebarMenuSub className='border-0 px-0 mx-0 overflox-w-hidden w-full'>
                            {item.items.map((item) => {
                              return (
                                <SidebarMenuSubItem
                                  key={item.href}
                                  className='w-full'
                                >
                                  <SidebarMenuSubButton
                                    className={cn('', {
                                      'bg-sidebar-accent': isMenuItemActive(
                                        fullPath,
                                        item.href,
                                        item.exact ||
                                          !!searchParams.get('team_id'),
                                      ),
                                    })}
                                    asChild
                                  >
                                    <Link
                                      className='w-full px-8'
                                      href={item.href}
                                      onClick={handleMenuLinkClick(item)}
                                    >
                                      <MenuItemLinkContent
                                        item={item}
                                        isPending={pendingHref === item.href}
                                      />
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              );
                            })}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  );
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      className={cn('', {
                        'bg-sidebar-accent': isMenuItemActive(
                          fullPath,
                          item.href,
                          item.exact || !!searchParams.get('team_id'),
                        ),
                      })}
                      asChild
                    >
                      <Link
                        href={item.href}
                        onClick={handleMenuLinkClick(item)}
                      >
                        <MenuItemLinkContent
                          item={item}
                          isPending={pendingHref === item.href}
                        />
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
          </SidebarMenu>
        </SidebarContent>
      </div>

      {/* `mt-auto` lo ancla al fondo: el contenedor de Sidebar es una columna
          flex de alto completo. */}
      {components?.footer && (
        <SidebarFooter className='mt-auto px-[0.875rem] pb-[1.125rem]'>
          <components.footer />
        </SidebarFooter>
      )}
    </Sidebar>
  );
};

export const SidebarDashboardLayout = ({
  children,
  logoutUser,
  items,
  nextSlugs = [],
  components,
}: {
  children: React.ReactNode;
  logoutUser: () => Promise<any>;
  items: MenuItem[];
  nextSlugs?: PathSlugs;
  components?: SidebarComponents;
}) => {
  const isNextSlug = useEvaluatePathSlug(nextSlugs);

  return (
    <SidebarProvider>
      {!isNextSlug && (
        <AppSidebar
          components={components}
          items={items}
          logoutUser={logoutUser}
        />
      )}

      <div
        {...{ [SCROLL_CONTAINER_ATTRIBUTE]: '' }}
        className={cn('w-full bg-stone-50 h-screen overflow-auto', {
          'p-4 md:p-10': !isNextSlug,
          'flex flex-col': !isNextSlug,
        })}
      >
        {/* Mobile Header with Sidebar Trigger */}
        {!isNextSlug && (
          <div className='md:hidden flex items-center justify-between mb-4'>
            <div className='flex items-center space-x-2'>
              {components?.logo ? (
                <components.logo />
              ) : (
                <PiramidLogo size='md' />
              )}
            </div>
            <SidebarTrigger className='h-7 w-7' />
          </div>
        )}
        <div
          className={cn('w-full', {
            'max-w-full': isNextSlug,
            'max-w-7xl md:mx-auto': !isNextSlug,
          })}
        >
          {children}
        </div>
      </div>
    </SidebarProvider>
  );
};
